import React, { useState } from "react";
import { toast } from "sonner";
import { Share2, Link as LinkIcon, Mail, X, Check } from "lucide-react";

/**
 * Social sharing buttons: WhatsApp, Email, Facebook, LinkedIn, Copy link.
 * On mobile, tries the native Web Share API first.
 *
 * Props:
 *   url:   absolute URL to share (defaults to window.location.href)
 *   title: page/entry title
 *   text:  short description (for WhatsApp/native share body)
 *   testid prefix for data-testid attributes
 */
export default function SocialShare({ url, title = "", text = "", testid = "share", compact = false, className = "" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const encUrl = encodeURIComponent(shareUrl);
  const encTitle = encodeURIComponent(title);
  const encText = encodeURIComponent(text ? `${title}\n\n${text}` : title);

  const links = {
    whatsapp: `https://api.whatsapp.com/send?text=${encText}%20${encUrl}`,
    email: `mailto:?subject=${encTitle}&body=${encText}%0A%0A${encUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}&quote=${encTitle}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`,
  };

  const nativeShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text, url: shareUrl });
        return true;
      } catch { /* user cancelled */ }
    }
    return false;
  };

  const openMenu = async () => {
    if (await nativeShare()) return;
    setOpen((o) => !o);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link kopiert");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Konnte Link nicht kopieren");
    }
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        onClick={openMenu}
        data-testid={`${testid}-toggle`}
        className="inline-flex items-center gap-2 border border-brand-line bg-white px-3.5 py-2 text-sm text-neutral-800 hover:bg-neutral-50 transition-colors"
      >
        <Share2 className="h-4 w-4" />
        {!compact && <span>Teilen</span>}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 z-50 bg-white border border-brand-line shadow-[0_8px_28px_rgba(0,0,0,0.10)] w-64 py-1" data-testid={`${testid}-menu`}>
            <div className="flex items-center justify-between px-3 py-2 border-b border-brand-line">
              <span className="text-xs uppercase tracking-[0.14em] text-neutral-500">Teilen</span>
              <button onClick={() => setOpen(false)} className="text-neutral-400 hover:text-neutral-700" aria-label="Schließen"><X className="h-3.5 w-3.5" /></button>
            </div>
            <a href={links.whatsapp} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-whatsapp`} className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-800 hover:bg-neutral-50">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="#25D366"><path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.5 0 .17 5.34.17 11.9c0 2.1.55 4.15 1.6 5.95L0 24l6.32-1.66a11.9 11.9 0 0 0 5.74 1.46h.01c6.55 0 11.89-5.34 11.89-11.9 0-3.18-1.24-6.16-3.44-8.42Zm-8.46 18.3a9.9 9.9 0 0 1-5.05-1.38l-.36-.22-3.75.98 1-3.66-.24-.38a9.85 9.85 0 0 1-1.51-5.22c0-5.45 4.44-9.89 9.9-9.89 2.65 0 5.13 1.03 7 2.9a9.83 9.83 0 0 1 2.9 7c0 5.45-4.44 9.87-9.89 9.87Zm5.42-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15s-.77.97-.94 1.17c-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.64-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.48.71.31 1.26.5 1.69.63.71.22 1.36.19 1.87.12.57-.08 1.76-.72 2.01-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35Z"/></svg>
              WhatsApp
            </a>
            <a href={links.email} data-testid={`${testid}-email`} className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-800 hover:bg-neutral-50">
              <Mail className="h-4 w-4 text-neutral-600" /> E-Mail
            </a>
            <a href={links.facebook} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-facebook`} className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-800 hover:bg-neutral-50">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="#1877F2"><path d="M22.68 0H1.32C.59 0 0 .58 0 1.31v21.38C0 23.42.59 24 1.32 24h11.5v-9.29H9.69v-3.62h3.13V8.41c0-3.1 1.89-4.79 4.66-4.79 1.33 0 2.46.1 2.79.14v3.24l-1.91.001c-1.5 0-1.79.71-1.79 1.76v2.31h3.58l-.47 3.62h-3.11V24h6.11c.73 0 1.32-.58 1.32-1.31V1.31C24 .58 23.41 0 22.68 0Z"/></svg>
              Facebook
            </a>
            <a href={links.linkedin} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-linkedin`} className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-800 hover:bg-neutral-50">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="#0A66C2"><path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.36V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.26 2.37 4.26 5.45v6.29ZM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13ZM7.12 20.45H3.55V9h3.57v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45C23.2 24 24 23.23 24 22.28V1.72C24 .77 23.2 0 22.22 0Z"/></svg>
              LinkedIn
            </a>
            <button onClick={copyLink} data-testid={`${testid}-copy`} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-800 hover:bg-neutral-50 border-t border-brand-line">
              {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <LinkIcon className="h-4 w-4 text-neutral-600" />}
              {copied ? "Kopiert" : "Link kopieren"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
